// Coluna da esquerda: a fila de conversas.
//
// Virtualizada com react-window (v2: `rowComponent` + `rowProps`). Altura de
// linha fixa de propósito — altura dinâmica é bem mais cara e aqui não precisa,
// já que a prévia é sempre de uma linha só, com reticências.

import React, { useCallback, useMemo } from 'react';
import { List, type RowComponentProps } from 'react-window';
import { AtSign, Check, CheckCheck, Clock, Search, Ticket, TriangleAlert, X } from 'lucide-react';
import type { ConversaLista, ConversaStatus } from '../tipos';
import type { Etiqueta } from '../apiOrganizacao';
import { carimboLista, nomeExibicao, previaMensagem } from '../formato';
import Avatar from './Avatar';

export const ALTURA_LINHA = 78;

interface Props {
  conversas: ConversaLista[];
  selecionada: string | null;
  onSelecionar: (id: string) => void;
  /** Fase 14 — etiquetas por conversa, para o ponto colorido da linha. */
  etiquetas?: Etiqueta[];
  etiquetasPorConversa?: Record<string, string[]>;
  busca: string;
  onBusca: (v: string) => void;
  filtro: ConversaStatus | null;
  onFiltro: (f: ConversaStatus | null) => void;
  /** Canais que têm conexão — só esses viram botão. */
  canais?: { chave: string; rotulo: string }[];
  /** Chave do grupo de canal selecionado; null = todos. */
  canal?: string | null;
  onCanal?: (c: string | null) => void;
  carregando: boolean;
  total: number;
}

const FILTROS: { chave: ConversaStatus | null; rotulo: string }[] = [
  { chave: null, rotulo: 'Todas' },
  { chave: 'open', rotulo: 'Abertas' },
  { chave: 'pending', rotulo: 'Aguardando' },
  { chave: 'resolved', rotulo: 'Resolvidas' },
];

/** Selo de entrega, só nas mensagens que saíram daqui. */
function SeloEnvio({ conversa }: { conversa: ConversaLista }) {
  if (conversa.ultima_mensagem_direcao !== 'outbound') return null;
  const s = conversa.ultima_mensagem_status;
  if (s === 'failed') return <TriangleAlert size={13} className="shrink-0 text-red-500" />;
  if (s === 'queued') return <Clock size={13} className="shrink-0 text-gray-400 dark:text-slate-500" />;
  if (s === 'read') return <CheckCheck size={13} className="shrink-0 text-sky-500" />;
  if (s === 'delivered') return <CheckCheck size={13} className="shrink-0 text-gray-400 dark:text-slate-500" />;
  return <Check size={13} className="shrink-0 text-gray-400 dark:text-slate-500" />;
}

interface LinhaProps {
  conversas: ConversaLista[];
  selecionada: string | null;
  onSelecionar: (id: string) => void;
  corPorTag: Map<string, Etiqueta>;
  etiquetasPorConversa: Record<string, string[]>;
}

function Linha({
  index, style, conversas, selecionada, onSelecionar, corPorTag, etiquetasPorConversa,
}: RowComponentProps<LinhaProps>) {
  const c = conversas[index];
  if (!c) return null;

  const ativa = c.id === selecionada;
  const naoLidas = c.unread_count > 0;
  // Ponto colorido, não chip com nome: a linha tem altura fixa (a lista é
  // virtualizada) e um chip empurraria a prévia da mensagem para fora. Três
  // pontos bastam para reconhecer o padrão; o nome está no painel da direita.
  const tagsDaLinha = (etiquetasPorConversa[c.id] ?? [])
    .map((id) => corPorTag.get(id))
    .filter((t): t is Etiqueta => !!t)
    .slice(0, 3);

  return (
    <div style={style} className="px-2">
      <button
        onClick={() => onSelecionar(c.id)}
        className={`group flex h-[70px] w-full items-center gap-3 rounded-xl px-3 text-left transition-colors ${
          ativa
            ? 'bg-primary/10 dark:bg-primary/20'
            : 'hover:bg-gray-100 dark:hover:bg-slate-700/50'
        }`}
      >
        <Avatar nome={c.contato_nome} telefone={c.contato_telefone} url={c.contato_avatar} chave={c.contact_id} />

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span
              className={`flex min-w-0 items-center gap-1 truncate text-sm ${
                naoLidas
                  ? 'font-bold text-gray-900 dark:text-white'
                  : 'font-semibold text-gray-700 dark:text-slate-200'
              }`}
            >
              {/* Chamado em andamento aparece como marca, não como texto: a
                  linha não tem espaço para o protocolo inteiro, e quem procura
                  por número usa a busca — que também casa com o protocolo. */}
              {c.ticket_protocolo && c.ticket_status !== 'fechado' && c.ticket_status !== 'resolvido' && (
                <Ticket size={11} className="shrink-0 text-primary dark:text-[#B4C77A]" aria-label="tem chamado aberto" />
              )}
              {/* Comentário de post (Fase 13) é conversa PÚBLICA. A marca fica
                  antes do nome porque é o que muda o que se pode escrever. */}
              {c.kind === 'comment_thread' && (
                <AtSign size={11} className="shrink-0 text-fuchsia-500" aria-label="comentário público" />
              )}
              <span className="truncate">{nomeExibicao(c.contato_nome, c.contato_telefone)}</span>
              {tagsDaLinha.map((t) => (
                <span
                  key={t.id}
                  title={t.nome}
                  className="h-1.5 w-1.5 shrink-0 rounded-full"
                  style={{ backgroundColor: t.cor }}
                />
              ))}
            </span>
            <span
              className={`shrink-0 text-[11px] tabular-nums ${
                naoLidas ? 'font-semibold text-primary dark:text-[#B4C77A]' : 'text-gray-400 dark:text-slate-500'
              }`}
            >
              {carimboLista(c.last_message_at)}
            </span>
          </div>

          <div className="mt-0.5 flex items-center gap-1.5">
            <SeloEnvio conversa={c} />
            <span
              className={`truncate text-xs ${
                naoLidas ? 'text-gray-700 dark:text-slate-300' : 'text-gray-500 dark:text-slate-400'
              }`}
            >
              {previaMensagem(c.ultima_mensagem_tipo, c.ultima_mensagem, c.ultima_mensagem_direcao)}
            </span>

            {naoLidas && (
              <span className="ml-auto shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                {c.unread_count > 99 ? '99+' : c.unread_count}
              </span>
            )}
            {!naoLidas && c.status === 'pending' && (
              <span className="ml-auto shrink-0 rounded-full bg-secondary/20 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-[#9A6A12] dark:text-secondary">
                aguardando
              </span>
            )}
          </div>
        </div>
      </button>
    </div>
  );
}

export default function ListaConversas({
  conversas, selecionada, onSelecionar, busca, onBusca, filtro, onFiltro, carregando, total,
  etiquetas = [], etiquetasPorConversa = {},
  canais = [], canal = null, onCanal,
}: Props) {
  const filtrando = !!(busca || filtro || canal);
  const chaveDaLinha = useCallback(
    (index: number, { conversas: lista }: LinhaProps) => lista[index]?.id ?? index,
    [],
  );
  const corPorTag = useMemo(() => new Map(etiquetas.map((t) => [t.id, t])), [etiquetas]);

  return (
    <section className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
      <div className="shrink-0 border-b border-gray-200 px-3 pb-2.5 pt-3 dark:border-slate-700">
        <div className="relative">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" />
          <input
            value={busca}
            onChange={(e) => onBusca(e.target.value)}
            placeholder="Buscar por nome, telefone ou mensagem"
            className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2 pl-9 pr-8 text-sm text-gray-800 placeholder:text-gray-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary dark:border-slate-600 dark:bg-slate-900/60 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:bg-slate-900"
          />
          {busca && (
            <button
              onClick={() => onBusca('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-slate-700"
              aria-label="Limpar busca"
            >
              <X size={13} />
            </button>
          )}
        </div>

        <div className="mt-2.5 flex items-center gap-1">
          {FILTROS.map((f) => (
            <button
              key={f.rotulo}
              onClick={() => onFiltro(f.chave)}
              className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                filtro === f.chave
                  ? 'bg-primary text-white'
                  : 'text-gray-500 hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-700'
              }`}
            >
              {f.rotulo}
            </button>
          ))}
        </div>

        {/* Com um canal só, o filtro não separa nada — não vale a linha. */}
        {canais.length > 1 && onCanal && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1">
            {[{ chave: null as string | null, rotulo: 'Todos os canais' }, ...canais].map((c) => (
              <button
                key={c.chave ?? 'todos'}
                onClick={() => onCanal(c.chave)}
                className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                  canal === c.chave
                    ? 'border-primary bg-primary/10 text-primary dark:text-primary'
                    : 'border-gray-200 text-gray-500 hover:bg-gray-100 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-700'
                }`}
              >
                {c.rotulo}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* O root do List se dimensiona com flexGrow + overflowY:auto, entao o pai
          PRECISA ser flex com altura limitada. Num container de bloco a lista
          nasceria com altura zero — visualmente vazia, sem erro nenhum. */}
      <div className="flex min-h-0 flex-1 flex-col py-1.5">
        {conversas.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-6 text-center">
            <p className="text-sm font-medium text-gray-500 dark:text-slate-400">
              {carregando ? 'Carregando…' : filtrando ? 'Nada com esse filtro' : 'Nenhuma conversa ainda'}
            </p>
            {!carregando && !filtrando && (
              <p className="mt-1 text-xs text-gray-400 dark:text-slate-500">
                Quando alguém mandar mensagem no WhatsApp, a conversa aparece aqui sozinha.
              </p>
            )}
            {!carregando && filtrando && total > 0 && (
              <p className="mt-1 text-xs text-gray-400 dark:text-slate-500">
                {total} {total === 1 ? 'conversa' : 'conversas'} no total.
              </p>
            )}
          </div>
        ) : (
          <List
            rowComponent={Linha}
            rowCount={conversas.length}
            rowHeight={ALTURA_LINHA}
            rowKey={chaveDaLinha}
            rowProps={{ conversas, selecionada, onSelecionar, corPorTag, etiquetasPorConversa }}
            overscanCount={6}
            defaultHeight={520}
            className="min-h-0 flex-1"
          />
        )}
      </div>
    </section>
  );
}
